import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, Wallet, ArrowUpRight, Trophy, Medal, Share2, Loader2 } from "lucide-react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import api from "@/lib/api";
import FranchiseEnquiry from "@/components/FranchiseEnquiry";
import TeamCard from "@/components/TeamCard";

const EASE = [0.22, 1, 0.36, 1];
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

function teamHonours(team, winners) {
  const TEAM_ALIASES = { "KC Bats": "KRICS" };
  const canon = (t) => TEAM_ALIASES[t] || t;
  const titles = winners.filter((w) => canon(w.champion) === team).map((w) => w.season);
  const runners = winners.filter((w) => canon(w.runner_up) === team).map((w) => w.season);
  return { titles, runners };
}

export default function Teams() {
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [winners, setWinners] = useState([]);
  const [cards, setCards] = useState([]);
  const [shareTeam, setShareTeam] = useState(null);
  const [sharing, setSharing] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    api.get("/auction/teams").then((r) => setTeams(r.data)).catch(() => {});
    api.get("/auction/players").then((r) => setPlayers(r.data)).catch(() => {});
    api.get("/winners").then((r) => setWinners(r.data)).catch(() => {});
    api.get("/stats/franchise-cards").then((r) => setCards(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!shareTeam) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      if (!cardRef.current || cancelled) return;
      try {
        const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, cacheBust: true });
        const blob = await (await fetch(dataUrl)).blob();
        const file = new File([blob], `${shareTeam.team.replace(/\s+/g, "-")}-ASL-franchise-card.png`, { type: "image/png" });
        const text = `${shareTeam.team} — All Star League franchise card | ${shareTeam.titles.length} title${shareTeam.titles.length === 1 ? "" : "s"}, ${shareTeam.runs} runs, ${shareTeam.wickets} wickets all-time`;
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: "All Star League franchise card", text });
          toast.success("Franchise card shared");
        } else {
          const a = document.createElement("a");
          a.href = dataUrl;
          a.download = file.name;
          a.click();
          toast.success("Franchise card downloaded — share it on WhatsApp or Instagram");
        }
      } catch (e) {
        if (e?.name !== "AbortError") toast.error("Couldn't create the card — try again");
      } finally {
        if (!cancelled) {
          setSharing(false);
          setShareTeam(null);
        }
      }
    }, 120);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [shareTeam]);

  return (
    <main className="pt-28 pb-24 bg-league-ink min-h-screen text-white" data-testid="teams-page">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <span className="text-[11px] font-bold uppercase tracking-[0.3em] text-league-red flex items-center gap-2">
          <Users size={14} /> Season 6 Franchises
        </span>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter mt-3 mb-14">
          THE <span className="text-outline-white">TEAMS</span>
        </h1>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {teams.map((t, i) => {
            const squad = players.filter((p) => p.status === "sold" && p.sold_to === t.team);
            const franchiseCard = cards.find((c) => c.team === t.team);
            return (
              <motion.div
                key={t.team}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.55, delay: i * 0.07, ease: EASE }}
                className="relative"
              >
                {franchiseCard && (
                  <button
                    onClick={() => {
                      if (sharing) return;
                      setSharing(true);
                      setShareTeam(franchiseCard);
                    }}
                    disabled={sharing}
                    data-testid={`team-share-${t.team.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    aria-label={`Share ${t.team} franchise card`}
                    title="Share franchise card"
                    className="absolute -top-3 right-4 z-10 w-9 h-9 rounded-full bg-league-blue text-white flex items-center justify-center shadow-lg transition-colors duration-200 hover:bg-league-red disabled:opacity-60 cursor-pointer"
                  >
                    {sharing && shareTeam?.team === t.team ? <Loader2 size={14} className="animate-spin" /> : <Share2 size={14} />}
                  </button>
                )}
                <Link
                  to={`/teams/${encodeURIComponent(t.team)}`}
                  data-testid={`team-card-${t.team.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="block bg-white/5 border border-white/10 rounded-2xl p-6 hover:-translate-y-1 hover:bg-white/10 hover:border-league-red/50 transition-[transform,background-color,border-color] duration-300 group"
                >
                  <div className="flex items-start justify-between mb-6">
                    <img src="/asl-logo-white.png" alt="" className="h-12 w-auto" />
                    <ArrowUpRight size={18} className="text-white/40 transition-colors duration-200 group-hover:text-league-red" />
                  </div>
                  <h2 className="font-oswald text-xl font-semibold leading-tight">{t.team}</h2>
                  {t.team === "KRICS" && (
                    <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-white/40 mt-0.5" data-testid="team-formerly-krics">formerly KC Bats</p>
                  )}
                  {(() => {
                    const { titles, runners } = teamHonours(t.team, winners);
                    return (titles.length > 0 || runners.length > 0) && (
                      <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`team-honours-${t.team.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                        {titles.map((s) => (
                          <span key={`t${s}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-yellow-400/15 border border-yellow-400/40 text-yellow-300 text-[10px] font-bold uppercase tracking-wider">
                            <Trophy size={11} /> S{s}.0
                          </span>
                        ))}
                        {runners.map((s) => (
                          <span key={`r${s}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/10 border border-white/20 text-white/60 text-[10px] font-bold uppercase tracking-wider">
                            <Medal size={11} /> S{s}.0
                          </span>
                        ))}
                      </div>
                    );
                  })()}
                  <div className="mt-5 space-y-2 text-sm">
                    <p className="flex items-center justify-between text-white/60">
                      <span className="flex items-center gap-2"><Users size={13} /> Squad</span>
                      <span className="font-oswald text-white font-semibold">{squad.length} players</span>
                    </p>
                    {t.purse_total > 0 && (
                      <p className="flex items-center justify-between text-white/60">
                        <span className="flex items-center gap-2"><Wallet size={13} /> Purse left</span>
                        <span className="font-oswald text-league-red font-semibold">{inr(t.purse_left)}</span>
                      </p>
                    )}
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-16">
          <FranchiseEnquiry dark />
        </div>
      </div>

      {shareTeam && (
        <div style={{ position: "fixed", left: "-2000px", top: 0, pointerEvents: "none" }} aria-hidden="true">
          <TeamCard ref={cardRef} card={shareTeam} />
        </div>
      )}
    </main>
  );
}
